import { combineReducers } from 'redux';
import {
  GET_REPORT,
  UPDATE_FILTERS,
  GET_FILTER_FIELDS,
  GET_STUDENTS_LIST,
  CLOSE_STUDENTS_LIST,
  SET_REPORT_STALE,
  SET_LAST_RUN,
  RESTORE_LAST_RUN,
  GET_GROWTH_MATRIX,
  CLEAR_GROWTH_MATRIX,
} from './aToF.actions';
import { ASYNC_ERROR, ASYNC_FINISH, ASYNC_START } from '../../../common';
import { getCurrentYear } from '../../../common/+store/utils';
import { GET_ASSESSMENT_LIST } from '../../../Assessments';
import { GET_FLIGHT_PLAN } from '../../../FlightPlan/+store/flightPlan';

const initialStatus = {
  message: '',
  error: '',
};
const status = (state = initialStatus, action) => {
  switch (action.type) {
    case ASYNC_FINISH:
      if (action.model === GET_REPORT) {
        return Object.assign({}, state, {
          message: action.data.message,
          error: '',
        });
      }
      if (action.model === GET_FILTER_FIELDS) {
        return Object.assign({}, state, {
          message: action.data.message,
          error: '',
        });
      }
      if (action.model === GET_ASSESSMENT_LIST) {
        return Object.assign({}, state, {
          message: action.data.message,
          error: '',
        });
      }
      return state;
    case ASYNC_ERROR:
      if (action.model === GET_REPORT) {
        return Object.assign({}, state, {
          message: '',
          error: action.error.message,
        });
      }
      if (action.model === GET_FILTER_FIELDS) {
        return Object.assign({}, state, {
          message: '',
          error: action.error.message,
        });
      }
      return state;
    default:
      return state;
  }
};

const loadingMsg = (state = null, action) => {
  switch (action.type) {
    case ASYNC_START:
      if (action.model === GET_REPORT) {
        return Object.assign({}, state, {
          report: 'Loading A-F Report ...',
        });
      }
      if (action.model === GET_FILTER_FIELDS) {
        return Object.assign({}, state, {
          filters: 'Loading Filter Fields ...',
        });
      }
      return state;
    case ASYNC_ERROR:
    case ASYNC_FINISH:
      if (action.model === GET_REPORT) {
        return Object.assign({}, state, {
          report: null,
        });
      }
      if (action.model === GET_FILTER_FIELDS) {
        return Object.assign({}, state, {
          filters: null,
        });
      }
      return state;
    default:
      return state;
  }
};

const reportData = (state = null, action) => {
  switch (action.type) {
    case ASYNC_FINISH:
      if (action.model === GET_REPORT) {
        return action.data || null;
      }
      return state;
    case ASYNC_START:
      if (action.model === GET_REPORT) {
        return null;
      }
      return state;
    default:
      return state;
  }
};

const reportError = (state = null, action) => {
  switch (action.type) {
    case ASYNC_FINISH:
      if (action.model === GET_REPORT) {
        return action.data.reportError || null;
      }
      return state;
    default:
      return state;
  }
};
const waitMessage = (state = null, action) => {
  switch (action.type) {
    case ASYNC_FINISH:
      if (action.model === GET_REPORT) {
        // reportError itself is rendered under the filters; a second generic
        // "There is an error" line added nothing, so just clear the wait text.
        return '';
      }
      return state;
    case ASYNC_START:
      if (action.model === GET_REPORT) {
        if (action && action.data && action.data.campusDistrictNumber && action.data.campusDistrictNumber.length == 6) {
          return 'I’m sorry to announce that your flight has been delayed. Please try running the district report again in a few minutes. If the problem persists, please contact support.';
        }
      }

      return state;

    default:
      return state;
  }
};
const filterFields = (state = null, action) => {
  switch (action.type) {
    case ASYNC_FINISH:
      if (action.model === GET_FILTER_FIELDS || action.model === GET_REPORT || action.model === GET_FLIGHT_PLAN) {
        return action.data.filterFields || state;
      }
      return state;
    default:
      return state;
  }
};

const showStudentsList = (state = null, action) => {
  if (action.type === ASYNC_FINISH && action.model === GET_STUDENTS_LIST) {
    var studentsByCount = {};
    if (action.data.students) {
      studentsByCount = action.data.students.reduce((a, c) => ((a[c] = (a[c] || 0) + 1), a), Object.create(null));
    }
    return {
      students: studentsByCount,
      versions: action.data.versions || null,
      rowLabel: action.data.rowLabel,
      indicatorLabel: action.data.indicatorLabel,
    };
  }
  if (action.type === CLOSE_STUDENTS_LIST) {
    return null;
  }
  return state;
};

const filters = (
  state = {
    assessmentSource: [],
    school: null,
    subject: ['all'],
    grade: ['all'],
    teacher: ['all'],
    studentGroup: ['all'],
    tag: ['all'],
    // Tags to show as extra far-right columns (subset of All Students). Distinct
    // from `tag` (which narrows the whole report); these add side-by-side columns.
    columnTags: [],
    schoolYear: getCurrentYear(),
  },
  action
) => {
  switch (action.type) {
    case UPDATE_FILTERS:
      return action.filters;
    default:
      return state;
  }
};

// True when the filter bar has been touched since the visible report was run, so each
// report page can show "click Get Report" instead of numbers that no longer match the
// filters above them. Cleared when a run starts (that run makes them match again) and by
// Cancel. The report itself is never dropped, so Cancel restores it instantly.
const reportStale = (state = false, action) => {
  switch (action.type) {
    case SET_REPORT_STALE:
      return !!action.stale;
    case RESTORE_LAST_RUN:
      return false;
    case ASYNC_START:
      if (action.model === GET_REPORT || action.model === GET_FLIGHT_PLAN) {
        return false;
      }
      return state;
    default:
      return state;
  }
};

// What produced the report on screen: `filters` for Cancel to restore, `option` for the
// filter component to restore (it holds the score option in local state), and a token that
// increments on every Cancel so that component can tell one restore from the next.
const lastRun = (state = { filters: null, option: null, restoreToken: 0 }, action) => {
  switch (action.type) {
    case SET_LAST_RUN:
      return { ...state, filters: action.filters, option: action.option };
    case RESTORE_LAST_RUN:
      return { ...state, restoreToken: state.restoreToken + 1 };
    default:
      return state;
  }
};

// The D2a matrix re-rowed on a chosen baseline. Null = showing the report's own
// prior-year STAAR rows. Any new report run drops it, since the columns it was
// paired against are gone.
const growthMatrix = (state = null, action) => {
  switch (action.type) {
    case ASYNC_START:
      if (action.model === GET_GROWTH_MATRIX) {
        return { loading: true, baseline: action.data && action.data.baseline, data: null, error: null };
      }
      if (action.model === GET_REPORT) {
        return null;
      }
      return state;
    case ASYNC_FINISH:
      if (action.model === GET_GROWTH_MATRIX) {
        return {
          loading: false,
          baseline: action.data.baseline,
          data: action.data.growthMatrix || null,
          error: action.data.reportError || (action.data.growthMatrix ? null : 'No growth matrix returned.'),
        };
      }
      return state;
    case ASYNC_ERROR:
      if (action.model === GET_GROWTH_MATRIX) {
        return {
          loading: false,
          baseline: state && state.baseline,
          data: null,
          error: (action.error && action.error.message) || 'Unable to build the growth matrix.',
        };
      }
      return state;
    case CLEAR_GROWTH_MATRIX:
      return null;
    default:
      return state;
  }
};

export const _reducer = combineReducers({
  status,
  loadingMsg,
  reportData,
  filters,
  filterFields,
  reportError,
  waitMessage,
  showStudentsList,
  reportStale,
  lastRun,
  growthMatrix,
});

export default 'studentsPage.reducers.js';
